import {convertCzechAccountToIban} from "./convertCzechAccountToIban";

export interface SpdPaymentData {
    accountNumber: string;
    variableSymbol?: number;
    specificSymbol?: string;
    constantSymbol?: string;
    amount?: number;
    message?: string;
    dueDate?: string;
}

enum SpdField {
    AccountNumber = "ACC",
    Amount = "AM",
    Currency = "CC",
    DueDate = "DT",
    Message = "MSG",
    VariableSymbol = "X-VS",
    SpecificSymbol = "X-SS",
    ConstantSymbol = "X-KS",
    PaymentId = "X-ID",
}

const SPD_HEADER = "SPD*1.0";
const SPD_FIELD_SEPARATOR = "*";
const SPD_CURRENCY_CZK = "CZK";
const SPD_MAX_AMOUNT = 9_999_999.99;
const SPD_MAX_MESSAGE_LENGTH = 60;
const SPD_MAX_NUMERIC_SYMBOL_LENGTH = 10;
const SPD_DUE_DATE_LENGTH = 8;
const SPD_DUE_DATE_PATTERN = /^20\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])/;
const AMOUNT_DECIMAL_PLACES = 2;
const AMOUNT_ROUNDING_FACTOR = 100;

function roundAmountToTwoDecimalPlacesHalfUp(amount: number): number {
    return Math.round(amount * AMOUNT_ROUNDING_FACTOR) / AMOUNT_ROUNDING_FACTOR;
}

function formatAmountOrThrowIfNegative(amount: number): string | undefined {
    if (amount < 0) {
        throw new Error(`Invalid amount: ${amount}`);
    }
    if (amount === 0 || amount > SPD_MAX_AMOUNT) {
        return undefined;
    }
    return roundAmountToTwoDecimalPlacesHalfUp(amount).toFixed(AMOUNT_DECIMAL_PLACES);
}

function sanitizeNumericSymbol(value: string | number): string | undefined {
    const digitsOnly = String(value).replace(/\D/g, "").substring(0, SPD_MAX_NUMERIC_SYMBOL_LENGTH);
    return digitsOnly.length > 0 ? digitsOnly : undefined;
}

function truncateDueDateIfValid(dueDate: string): string | undefined {
    if (dueDate.length < SPD_DUE_DATE_LENGTH) {
        return undefined;
    }
    const truncated = dueDate.substring(0, SPD_DUE_DATE_LENGTH);
    if (!SPD_DUE_DATE_PATTERN.test(truncated)) {
        return undefined;
    }
    return truncated;
}

function encodeAsterisksForSpd(value: string): string {
    return value.replace(/\*/g, "%2A");
}

export function buildSpdString({
    accountNumber,
    variableSymbol,
    specificSymbol,
    constantSymbol,
    amount,
    message,
    dueDate,
}: SpdPaymentData, paymentId?: number): string {
    const fields: string[] = [SPD_HEADER];

    fields.push(`${SpdField.AccountNumber}:${convertCzechAccountToIban(accountNumber)}`);

    if (amount != null) {
        const formattedAmount = formatAmountOrThrowIfNegative(amount);
        if (formattedAmount !== undefined) {
            fields.push(`${SpdField.Amount}:${formattedAmount}`);
        }
    }

    fields.push(`${SpdField.Currency}:${SPD_CURRENCY_CZK}`);

    if (dueDate) {
        const truncatedDueDate = truncateDueDateIfValid(dueDate);
        if (truncatedDueDate !== undefined) {
            fields.push(`${SpdField.DueDate}:${truncatedDueDate}`);
        }
    }

    if (message) {
        fields.push(`${SpdField.Message}:${encodeAsterisksForSpd(message.trim().substring(0, SPD_MAX_MESSAGE_LENGTH))}`);
    }

    if (variableSymbol != null) {
        const sanitized = sanitizeNumericSymbol(variableSymbol);
        if (sanitized !== undefined) {
            fields.push(`${SpdField.VariableSymbol}:${sanitized}`);
        }
    }

    if (specificSymbol != null) {
        const sanitized = sanitizeNumericSymbol(specificSymbol);
        if (sanitized !== undefined) {
            fields.push(`${SpdField.SpecificSymbol}:${sanitized}`);
        }
    }

    if (constantSymbol != null) {
        const sanitized = sanitizeNumericSymbol(constantSymbol);
        if (sanitized !== undefined) {
            fields.push(`${SpdField.ConstantSymbol}:${sanitized}`);
        }
    }

    if (paymentId != null) {
        fields.push(`${SpdField.PaymentId}:${paymentId}`);
    }

    return fields.join(SPD_FIELD_SEPARATOR) + SPD_FIELD_SEPARATOR;
}
